import React, { createContext, useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";

type ThemePreference = "light" | "dark" | "system";

interface ThemeContextType {
  themePreference: ThemePreference;
  theme: "light" | "dark"; // The resolved theme being applied
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themePreference, setThemePreference] = useState<ThemePreference>(() => {
    const savedTheme = Cookies.get("theme") as ThemePreference;
    return savedTheme || "system"; // Default to system preference
  });

  const [theme, setTheme] = useState<"light" | "dark">(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return "light";
  });

  // Update theme based on themePreference
  useEffect(() => {
    let resolvedTheme: "light" | "dark";

    if (themePreference === "system") {
      // Use system preference
      if (typeof window !== "undefined" && window.matchMedia) {
        resolvedTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
      } else {
        resolvedTheme = "light";
      }
    } else {
      // Use explicit theme choice
      resolvedTheme = themePreference;
    }

    setTheme(resolvedTheme);

    // Apply to DOM
    if (resolvedTheme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }

    // Save preference
    Cookies.set("theme", themePreference, { expires: 365 });
  }, [themePreference]);

  // Listen for system theme changes when using system preference
  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

      const handleSystemThemeChange = (e: MediaQueryListEvent) => {
        if (themePreference === "system") {
          setTheme(e.matches ? "dark" : "light");
          // Apply to DOM immediately
          if (e.matches) {
            document.documentElement.classList.add("dark");
          } else {
            document.documentElement.classList.remove("dark");
          }
        }
      };

      mediaQuery.addEventListener("change", handleSystemThemeChange);

      return () => {
        mediaQuery.removeEventListener("change", handleSystemThemeChange);
      };
    }
  }, [themePreference]);

  const toggleTheme = () => {
    // Toggle based on current visual state, not logical state
    if (theme === "light") {
      setThemePreference("dark");
    } else {
      setThemePreference("light");
    }
  };

  return <ThemeContext.Provider value={{ themePreference, theme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
